/**
 * Sidebar - Sidebar layout (sidebar + compact mode).
 */
import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  X, ChevronDown, LogOut, Search as SearchIcon, Star,
  PanelLeftClose, PanelLeftOpen, Lock,
} from "lucide-react";
import { NAV_GROUPS, PERMANENT_LOGO } from "./navConfig";
import UserAvatar from "../../../components/UserAvatar";
import { TrialBanner } from "./TrialBanner";

export function Sidebar({
  user, branding, layout, prefs, favoriteModules, toggleFavorite,
  onClose, onLogout, onOpenSearch, isMobile = false, onToggleCompact,
}) {
  const isCompact = layout === "compact" && !isMobile;

  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const s = localStorage.getItem("sidebar_groups");
      return s ? JSON.parse(s) : NAV_GROUPS.filter(g => g.defaultOpen).reduce((a, g) => ({ ...a, [g.key]: true }), {});
    } catch { return { main: true, business: true }; }
  });

  const toggleGroup = (key) => {
    setOpenGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem("sidebar_groups", JSON.stringify(next));
      return next;
    });
  };

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  // iter413c-13 — Modules always visible (dashboard + personal settings).
  // Admin-only pages (users/roles/licences/audit_log/etc) are filtered by
  // adminOnly flag, no need to whitelist them here.
  const ALWAYS_VISIBLE_FOR_ADMIN = new Set([
    "dashboard", "settings", "advanced", "trash",
  ]);

  const tenantActiveModules = user?.tenant_active_modules || null;
  const isTenantScoped = !!user?.tenant_id && user?.role !== "super_admin";

  const filteredGroups = NAV_GROUPS.filter(g => {
    if (g.adminOnly && !isAdmin) return false;
    return true;
  }).map(g => ({
    ...g,
    items: g.items.filter(item => {
      if (item.adminOnly && !isAdmin) return false;
      if (prefs.visible_modules && !prefs.visible_modules.includes(item.moduleKey)) return false;
      // iter413au — Demo users see ALL non-admin modules; locked ones are marked
      // and trigger a "Zažádat o zapnutí" request handler instead of navigating.
      if (user?.role === "demo") {
        return !item.adminOnly;
      }
      // iter413c-13 — Tenant-scoped users: filter by tenant_active_modules from licences
      if (isTenantScoped && Array.isArray(tenantActiveModules)) {
        if (!ALWAYS_VISIBLE_FOR_ADMIN.has(item.moduleKey) && !tenantActiveModules.includes(item.moduleKey)) {
          return false;
        }
      }
      if (user?.allowed_modules?.length > 0 && !user.allowed_modules.includes(item.moduleKey) && item.moduleKey !== "dashboard" && item.moduleKey !== "settings") return false;
      return true;
    }).map(item => {
      // Tag demo-locked items so the renderer can dim them and intercept clicks
      if (user?.role === "demo" && !user.allowed_modules?.includes(item.moduleKey)) {
        return { ...item, _locked: true };
      }
      return item;
    }),
  })).filter(g => g.items.length > 0);

  const handleRequestModule = async (moduleKey, label) => {
    try {
      const { default: axios } = await import("axios");
      const { toast } = await import("sonner");
      axios.post("/api/v1/demo/telemetry", {
        event: "module_request",
        meta: { module_key: moduleKey, module_label: label },
      }, { withCredentials: true }).catch(() => {});
      await axios.post("/api/v1/demo/lead", {
        name: user.name || "Demo user",
        email: user.email || "demo@local",
        phone: "",
        message: `Zájem o modul: ${label} (key: ${moduleKey}) — chci zapnout v plné verzi.`,
      }, { withCredentials: true });
      toast.success(`Žádost o modul "${label}" odeslána. Ozveme se ti.`);
    } catch (e) {
      // ignore — telemetry already best-effort
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`p-4 border-b border-slate-700/50 ${isCompact ? "flex justify-center" : ""}`}>
        <div className={`flex items-center ${isCompact ? "justify-center" : "gap-2.5"}`}>
          {branding.company_logo ? (
            <img src={branding.company_logo || PERMANENT_LOGO} alt={branding.company_name || "Logo"}
              className={`${isCompact ? "h-7 w-7 object-cover rounded" : "h-9 w-auto max-w-[140px] object-contain"} brightness-0 invert`} />
          ) : (
            <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xs">SG</span>
            </div>
          )}
          {!isCompact && !branding.company_logo && (
            <div>
              <div className="font-heading font-semibold text-white text-sm">{branding.company_name || "Solar Green"}</div>
              <div className="text-xs text-slate-400">Administrace</div>
            </div>
          )}
        </div>
      </div>

      {/* Search trigger */}
      {!isCompact && (
        <div className="px-2 pt-2">
          <button
            onClick={onOpenSearch}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-slate-400 bg-slate-800/60 hover:bg-slate-800 hover:text-slate-200 transition-colors border border-slate-700/40"
            data-testid="sidebar-search-btn"
          >
            <SearchIcon className="w-4 h-4" />
            <span className="flex-1 text-left text-xs">Hledat...</span>
            <kbd className="text-[9px] font-mono bg-slate-700/60 px-1.5 py-0.5 rounded text-slate-500">&#8984;K</kbd>
          </button>
        </div>
      )}
      {isCompact && (
        <div className="px-2 pt-2">
          <button
            onClick={onOpenSearch}
            className="w-full flex items-center justify-center py-2 rounded-xl text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
            title="Hledat (Ctrl+K)"
            data-testid="sidebar-search-btn"
          >
            <SearchIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Favorites */}
      {favoriteModules.length > 0 && (
        <div className="px-2 pt-2" data-testid="favorites-section">
          {!isCompact && (
            <div className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400/70">
              <Star className="w-3 h-3" />
              <span>Oblíbené</span>
            </div>
          )}
          {isCompact && <div className="border-t border-amber-500/20 my-1" />}
          <div className="space-y-0.5">
            {NAV_GROUPS.flatMap(g => g.items)
              .filter(item => favoriteModules.includes(item.moduleKey))
              .map(item => {
                const { path, label, icon: Icon } = item;
                return (
                  <NavLink key={`fav-${path}`} to={path} onClick={onClose}
                    title={isCompact ? `${label} (oblíbené)` : undefined}
                    className={({ isActive }) =>
                      `flex items-center ${isCompact ? "justify-center" : ""} gap-3 ${isCompact ? "px-2 py-2" : "px-3 py-1.5"} rounded-xl text-sm font-medium transition-colors duration-150 ${isActive ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-slate-700/50 hover:text-white"}`
                    }
                    data-testid={`fav-nav-${item.moduleKey}`}>
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {!isCompact && <span className="flex-1">{label}</span>}
                    {!isCompact && <Star className="w-3 h-3 text-amber-400 fill-amber-400 flex-shrink-0" />}
                  </NavLink>
                );
              })}
          </div>
        </div>
      )}

      {/* iter413c-14 — Trial countdown banner (only for tenant-scoped users) */}
      <TrialBanner user={user} compact={isCompact} />

      {/* Grouped Nav */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto overflow-x-hidden">
        {filteredGroups.map(group => {
          const GroupIcon = group.icon;
          const isOpen = openGroups[group.key];
          return (
            <div key={group.key} data-testid={`nav-group-${group.key}`}>
              {!isCompact ? (
                <button onClick={() => toggleGroup(group.key)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300 hover:bg-slate-800/50 transition-colors"
                  data-testid={`nav-group-toggle-${group.key}`}>
                  <GroupIcon className="w-3.5 h-3.5" />
                  <span className="flex-1 text-left">{group.label}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                </button>
              ) : (
                <div className="border-t border-slate-700/30 my-1.5" />
              )}
              {(isCompact || isOpen) && (
                <div className={`${isCompact ? "" : "ml-1"} space-y-0.5`}>
                  {group.items.map(item => {
                    const { path, label, icon: Icon, badge, moduleKey, _locked } = item;
                    const isFav = favoriteModules.includes(moduleKey);
                    if (_locked) {
                      return (
                        <div key={path} className="group flex items-center">
                          <button
                            onClick={() => handleRequestModule(moduleKey, label)}
                            title={isCompact ? `${label} (zažádat o zapnutí)` : "Tento modul není v demu povolen — klikni pro žádost o zapnutí"}
                            data-testid={`nav-locked-${label.toLowerCase().replace(/[/ ]/g, '-')}`}
                            className={`flex-1 flex items-center ${isCompact ? "justify-center" : ""} gap-3 ${isCompact ? "px-2 py-2" : "px-3 py-1.5"} rounded-xl text-sm font-medium text-slate-500 hover:text-amber-300 hover:bg-amber-500/10 transition-all duration-200 cursor-pointer`}
                          >
                            <Icon className="w-4 h-4 flex-shrink-0 opacity-60" />
                            {!isCompact && (
                              <>
                                <span className="flex-1 line-through decoration-slate-600 opacity-70">{label}</span>
                                <span className="text-[9px] font-bold bg-amber-500/20 text-amber-300 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
                                  <Lock className="w-2.5 h-2.5" /> ZÁMEK
                                </span>
                              </>
                            )}
                            {isCompact && (
                              <Lock className="w-2.5 h-2.5 absolute top-0 right-0 text-amber-300" />
                            )}
                          </button>
                        </div>
                      );
                    }
                    return (
                      <div key={path} className="group flex items-center">
                        <NavLink to={path} onClick={onClose}
                          title={isCompact ? label : undefined}
                          className={({ isActive }) =>
                            `flex-1 flex items-center ${isCompact ? "justify-center" : ""} gap-3 ${isCompact ? "px-2 py-2" : "px-3 py-1.5"} rounded-xl text-sm font-medium transition-all duration-200 ${isActive ? "bg-emerald-600 text-white nav-active-glow" : "text-slate-300 hover:bg-slate-700/50 hover:text-white hover:translate-x-0.5"}`
                          }
                          data-testid={`nav-${label.toLowerCase().replace(/[/ ]/g, '-')}`}>
                          <Icon className="w-4 h-4 flex-shrink-0" />
                          {!isCompact && <span className="flex-1">{label}</span>}
                          {!isCompact && badge && <span className="text-[9px] font-bold bg-violet-500 text-white px-1.5 py-0.5 rounded-full">{badge}</span>}
                        </NavLink>
                        {!isCompact && (
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleFavorite(moduleKey); }}
                            className={`p-1 rounded-lg transition-all ${isFav ? "text-amber-400 opacity-100" : "text-slate-600 opacity-0 group-hover:opacity-100 hover:text-amber-400"}`}
                            title={isFav ? "Odebrat z oblíbených" : "Přidat do oblíbených"}
                            data-testid={`fav-toggle-${moduleKey}`}
                          >
                            <Star className={`w-3 h-3 ${isFav ? "fill-amber-400" : ""}`} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User + Logout */}
      <div className="p-3 border-t border-slate-700/50">
        {!isCompact && user && (
          <div className="flex items-center gap-2.5 px-2 mb-3" data-testid="sidebar-user">
            <UserAvatar user={user} size="sm" showTitle={false} />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-white truncate">{user.name}</div>
              <div className="text-xs text-slate-400 truncate">{user.email}</div>
            </div>
          </div>
        )}
        {/* iter413r — Collapse/expand toggle right next to Logout */}
        <button onClick={onToggleCompact}
          title={isCompact ? "Rozbalit menu" : "Sbalit menu (jen ikony)"}
          className={`w-full flex items-center ${isCompact ? "justify-center" : ""} gap-2 px-3 py-2 rounded-xl text-sm text-slate-400 hover:text-emerald-400 hover:bg-slate-700/50 transition-colors mb-1`}
          data-testid="sidebar-collapse-toggle">
          {isCompact ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          {!isCompact && "Sbalit menu"}
        </button>
        <button onClick={onLogout} title={isCompact ? "Odhlásit se" : undefined}
          className={`w-full flex items-center ${isCompact ? "justify-center" : ""} gap-2 px-3 py-2 rounded-xl text-sm text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors`}
          data-testid="logout-btn">
          <LogOut className="w-4 h-4" />
          {!isCompact && "Odhlásit se"}
        </button>
      </div>
    </div>
  );
}

export function MobileSidebarOverlay({ children, onClose }) {
  return (
    <div className="lg:hidden fixed inset-0 z-50 flex">
      <div className="fixed inset-0 sidebar-overlay" onClick={onClose} />
      <aside className="relative w-56 bg-slate-900 flex flex-col z-10 animate-slide-in-left">
        <button onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
        {children}
      </aside>
    </div>
  );
}
